"use client";

/**
 * L'ONGLET « ACHATS » : les factures des fournisseurs reçues par courriel.
 *
 * Une année à la fois : totaux HT / TVA / TTC, la TVA déductible mois par
 * mois (par date de facture), la liste avec le PDF, et l'export CSV pour le
 * comptable. Une facture « à compléter » (IA absente ou en échec) ou « à
 * vérifier » se corrige ici, à la main. Les pièces écartées (pas des
 * factures, doublons) restent consultables et peuvent être reprises.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useData } from "@/components/data-provider";
import { listerAchats, corrigerAchat, ignorerAchat, joindreJustificatifsAchatsMaintenant, type LigneAchat } from "@/lib/actions/achats";
import type { RapportJustificatifsAchats } from "@/lib/achats/qonto";
import { toast } from "sonner";
import { ShoppingCart, Download, FileText, Loader2, Pencil, EyeOff, Eye, Settings, Paperclip } from "lucide-react";

const euros = (n: number | null) => (n == null ? "—" : n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const jour = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { timeZone: "UTC" }) : "—");
const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

const STATUTS: Record<string, { libelle: string; classe: string }> = {
  lue: { libelle: "Lue", classe: "text-emerald-400" },
  a_verifier: { libelle: "À vérifier", classe: "text-amber-400" },
  a_completer: { libelle: "À compléter", classe: "text-amber-400" },
  ignoree: { libelle: "Écartée", classe: "text-muted-foreground" },
};

function csv(lignes: LigneAchat[]): string {
  const champ = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const nombre = (n: number | null) => (n == null ? "" : n.toFixed(2).replace(".", ","));
  const entete = ["Date", "Fournisseur", "Numéro", "HT", "TVA", "TTC", "Devise d'origine", "Montant d'origine", "Statut", "Paiement", "Payée le", "Fichier"];
  const corps = lignes.map((l) =>
    [jour(l.dateFacture), l.fournisseurNom, l.numero, nombre(l.totalHT), nombre(l.totalTVA), nombre(l.totalTTC), l.devise, nombre(l.totalOrigine), STATUTS[l.statut]?.libelle ?? l.statut, { payee: "Payée", a_payer: "À payer", en_retard: "En retard" }[l.statutPaiement ?? ""] ?? "", l.datePaiement ? jour(l.datePaiement) : "", l.nomFichier]
      .map(champ)
      .join(";")
  );
  return [entete.join(";"), ...corps].join("\n");
}

function Correction({ ligne, onFini }: { ligne: LigneAchat; onFini: () => void }) {
  const { societe } = useData();
  const [v, setV] = useState({
    fournisseurNom: ligne.fournisseurNom ?? "",
    numero: ligne.numero ?? "",
    dateFacture: ligne.dateFacture?.slice(0, 10) ?? "",
    totalHT: ligne.totalHT?.toString() ?? "",
    totalTVA: ligne.totalTVA?.toString() ?? "",
    totalTTC: ligne.totalTTC?.toString() ?? "",
  });
  const [enCours, setEnCours] = useState(false);
  const nombre = (s: string) => Number(s.replace(",", "."));
  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societe?.id) return;
    setEnCours(true);
    try {
      const res = await corrigerAchat(societe.id, ligne.id, {
        fournisseurNom: v.fournisseurNom,
        numero: v.numero,
        dateFacture: v.dateFacture,
        totalHT: nombre(v.totalHT),
        totalTVA: nombre(v.totalTVA),
        totalTTC: nombre(v.totalTTC),
      });
      if (res.success) { toast.success("Facture enregistrée."); onFini(); }
      else toast.error(res.error);
    } finally {
      setEnCours(false);
    }
  };
  const champ = (cle: keyof typeof v, libelle: string, type = "text") => (
    <label className="space-y-1 text-xs">
      <span className="text-muted-foreground">{libelle}</span>
      <input type={type} value={v[cle]} onChange={(e) => setV({ ...v, [cle]: e.target.value })} required={cle !== "numero"} className="w-full h-9 px-2 rounded-lg border border-border bg-background text-sm text-foreground" />
    </label>
  );
  return (
    <form onSubmit={enregistrer} className="grid gap-2 sm:grid-cols-3 p-3 rounded-lg border border-border bg-muted/30">
      {champ("fournisseurNom", "Fournisseur")}
      {champ("numero", "Numéro")}
      {champ("dateFacture", "Date", "date")}
      {champ("totalHT", "HT (€)")}
      {champ("totalTVA", "TVA (€)")}
      {champ("totalTTC", "TTC (€)")}
      <div className="sm:col-span-3 flex gap-2">
        <button type="submit" disabled={enCours} className="h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50">
          {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
        </button>
        <button type="button" onClick={onFini} className="h-9 px-4 rounded-lg border border-border text-sm">Annuler</button>
      </div>
    </form>
  );
}

export function Achats() {
  const { societe } = useData();
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [lignes, setLignes] = useState<LigneAchat[] | null>(null);
  const [voirEcartees, setVoirEcartees] = useState(false);
  const [edition, setEdition] = useState<string | null>(null);
  const [fournisseur, setFournisseur] = useState<string>("");
  const [justif, setJustif] = useState<RapportJustificatifsAchats | null>(null);
  const [enCoursJustif, setEnCoursJustif] = useState(false);

  // D'abord une simulation (rien n'est écrit), puis, sur confirmation, le dépôt.
  const joindreDansQonto = async (simulation: boolean) => {
    if (!societe?.id) return;
    setEnCoursJustif(true);
    try {
      const res = await joindreJustificatifsAchatsMaintenant(societe.id, simulation);
      if (!res.success) toast.error(res.error);
      else if (res.data.erreur) toast.error(res.data.erreur);
      else if (!simulation) toast.success(`${res.data.joints.length} justificatif(s) joint(s) dans Qonto.`);
      if (res.success) setJustif(res.data);
      await charger();
    } finally {
      setEnCoursJustif(false);
    }
  };

  const charger = useCallback(async () => {
    if (!societe?.id) return;
    const res = await listerAchats(societe.id, annee);
    if (res.success) setLignes(res.data);
    else toast.error(res.error);
  }, [societe?.id, annee]);

  useEffect(() => { setLignes(null); charger(); }, [charger]);

  const nomDe = (l: LigneAchat) => l.fournisseurNom ?? "Fournisseur inconnu";
  // Les fournisseurs de l'année, du plus gros total au plus petit.
  const fournisseurs = useMemo(() => {
    const t = new Map<string, number>();
    for (const l of lignes ?? []) if (l.statut !== "ignoree") t.set(nomDe(l), (t.get(nomDe(l)) ?? 0) + (l.totalTTC ?? 0));
    return [...t.entries()].sort((a, b) => b[1] - a[1]).map(([nom]) => nom);
  }, [lignes]);
  const duFournisseur = useMemo(() => (lignes ?? []).filter((l) => !fournisseur || nomDe(l) === fournisseur), [lignes, fournisseur]);
  const retenues = useMemo(() => duFournisseur.filter((l) => l.statut !== "ignoree"), [duFournisseur]);
  const affichees = voirEcartees ? duFournisseur : retenues;
  const ecartees = duFournisseur.length - retenues.length;
  const somme = (k: "totalHT" | "totalTVA" | "totalTTC") => retenues.reduce((s, l) => s + Math.round((l[k] ?? 0) * 100), 0) / 100;
  const parMois = useMemo(() => {
    const m = Array.from({ length: 12 }, () => ({ ht: 0, tva: 0, n: 0 }));
    for (const l of retenues) {
      if (!l.dateFacture || l.totalTVA == null) continue;
      const i = new Date(l.dateFacture).getUTCMonth();
      m[i].ht += Math.round((l.totalHT ?? 0) * 100);
      m[i].tva += Math.round(l.totalTVA * 100);
      m[i].n++;
    }
    return m;
  }, [retenues]);
  const aTraiter = retenues.filter((l) => l.statut === "a_completer" || l.statut === "a_verifier").length;

  const exporter = () => {
    const blob = new Blob([`﻿${csv(retenues)}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `achats_${societe?.nom ?? "societe"}_${annee}${fournisseur ? `_${fournisseur.replace(/[^\p{L}\p{N}]+/gu, "-")}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const basculer = async (l: LigneAchat) => {
    if (!societe?.id) return;
    const res = await ignorerAchat(societe.id, l.id, l.statut !== "ignoree");
    if (res.success) await charger();
    else toast.error(res.error);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-28 sm:py-6 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><ShoppingCart className="h-6 w-6 text-teal-400" /></div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Achats</h1>
            <p className="text-xs text-muted-foreground">Les factures des fournisseurs reçues par courriel.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={fournisseur} onChange={(e) => setFournisseur(e.target.value)} aria-label="Fournisseur" className="h-9 px-2 rounded-lg border border-border bg-background text-sm text-foreground max-w-[12rem]">
            <option value="">Tous les fournisseurs</option>
            {fournisseurs.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="h-9 px-2 rounded-lg border border-border bg-background text-sm text-foreground">
            {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button type="button" onClick={() => joindreDansQonto(true)} disabled={enCoursJustif} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border text-sm hover:bg-muted disabled:opacity-50">
            {enCoursJustif ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} <span className="hidden sm:inline">Justificatifs Qonto</span>
          </button>
          <button type="button" onClick={exporter} disabled={!retenues.length} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border text-sm hover:bg-muted disabled:opacity-50">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          <Link href="/settings?view=ACHATS" className="inline-flex items-center h-9 px-3 rounded-lg border border-border text-sm hover:bg-muted" aria-label="Réglages des achats">
            <Settings className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {lignes === null ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            {[["Total HT", somme("totalHT")], ["TVA déductible", somme("totalTVA")], ["Total TTC", somme("totalTTC")]].map(([libelle, v]) => (
              <div key={libelle as string} className="glass-card p-4 rounded-xl border border-border">
                <p className="text-xs text-muted-foreground">{libelle}</p>
                <p className="text-lg font-bold text-foreground tabular-nums">{euros(v as number)}</p>
              </div>
            ))}
          </div>
          {aTraiter > 0 && <p className="text-sm text-amber-400">{aTraiter} facture(s) à compléter ou à vérifier.</p>}
          {justif && (
            <div className="glass-card p-4 rounded-xl border border-border text-sm space-y-1">
              <p className="font-medium text-foreground">Justificatifs dans Qonto{justif.simulation ? " — simulation, rien n'est encore écrit" : ""}</p>
              {justif.erreur && <p className="text-red-400">{justif.erreur}</p>}
              <p className="text-muted-foreground">
                {justif.joints.length} {justif.simulation ? "à joindre" : "joint(s)"} · {justif.dejaJustifiees} déjà justifiée(s) · {justif.sansOperation} sans paiement trouvé
              </p>
              {justif.joints.length > 0 && (
                <ul className="text-xs text-muted-foreground list-disc list-inside">
                  {justif.joints.map((j, i) => <li key={i}>{j.fournisseur ?? "?"} {j.numero ?? ""} → « {j.operation} » du {jour(j.date)} ({j.raison})</li>)}
                </ul>
              )}
              {justif.aVerifier.length > 0 && <p className="text-xs text-amber-400">À vérifier : {justif.aVerifier.join(" · ")}</p>}
              {justif.erreurs.length > 0 && <p className="text-xs text-red-400">Erreurs : {justif.erreurs.slice(0, 3).join(" · ")}</p>}
              {justif.simulation && justif.joints.length > 0 && (
                <button type="button" onClick={() => joindreDansQonto(false)} disabled={enCoursJustif} className="mt-2 inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {enCoursJustif ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Joindre ces {justif.joints.length} justificatif(s) dans Qonto
                </button>
              )}
            </div>
          )}

          <div className="glass-card p-4 rounded-xl border border-border overflow-x-auto">
            <h2 className="text-sm font-semibold text-foreground mb-2">TVA déductible par mois (date de facture){fournisseur ? ` — ${fournisseur}` : ""}</h2>
            <table className="w-full text-xs tabular-nums">
              <thead><tr className="text-muted-foreground"><th className="text-left font-normal py-1">Mois</th><th className="text-right font-normal">Factures</th><th className="text-right font-normal">HT</th><th className="text-right font-normal">TVA</th></tr></thead>
              <tbody>
                {parMois.map((m, i) => m.n > 0 && (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1">{MOIS[i]} {annee}</td>
                    <td className="text-right">{m.n}</td>
                    <td className="text-right">{euros(m.ht / 100)}</td>
                    <td className="text-right">{euros(m.tva / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2">
            {affichees.length === 0 && <p className="text-sm text-muted-foreground">Aucune facture d'achat{fournisseur ? ` de ${fournisseur}` : ""} pour {annee}.</p>}
            {affichees.map((l) => (
              <div key={l.id} className="glass-card p-3 rounded-xl border border-border space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{l.fournisseurNom ?? l.expediteur ?? "Fournisseur inconnu"}</p>
                    <p className="text-xs text-muted-foreground">
                      {jour(l.dateFacture ?? l.recuLe)} · {l.numero ?? "sans numéro"} ·{" "}
                      <span className={STATUTS[l.statut]?.classe}>{STATUTS[l.statut]?.libelle ?? l.statut}</span>
                      {l.lecture === "ia" ? " · lue par l'IA" : l.lecture === "manuel" ? " · saisie à la main" : l.lecture === "les_indes" ? " · portail Les Indés" : ""}
                      {l.rapproche ? " · rapprochée Qonto" : ""}
                      {l.statutPaiement === "payee" ? <span className="text-emerald-400"> · payée{l.datePaiement ? ` le ${jour(l.datePaiement)}` : ""}</span>
                        : l.statutPaiement === "en_retard" ? <span className="text-red-400"> · en retard</span>
                        : l.statutPaiement === "a_payer" ? <span className="text-amber-400"> · à payer</span> : null}
                    </p>
                    {l.remarque && <p className="text-xs text-muted-foreground italic">{l.remarque}</p>}
                    {l.dashboardErreur && <p className="text-xs text-amber-400">Non envoyée à dashboard-media : {l.dashboardErreur}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-foreground tabular-nums">{euros(l.totalTTC)}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">TVA {euros(l.totalTVA)}</p>
                    {l.devise !== "EUR" && l.totalOrigine != null && <p className="text-xs text-muted-foreground">{l.totalOrigine.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {l.devise}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a href={l.source === "les_indes" ? `/api/achats/retenue/${l.id.replace(/^ret_/, "")}/pdf` : `/api/achats/${l.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 h-8 px-2 rounded-lg border border-border text-xs hover:bg-muted">
                    <FileText className="h-3.5 w-3.5" /> PDF
                  </a>
                  {l.statut !== "ignoree" && l.source === "mail" && (
                    <button type="button" onClick={() => setEdition(edition === l.id ? null : l.id)} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg border border-border text-xs hover:bg-muted">
                      <Pencil className="h-3.5 w-3.5" /> {l.statut === "a_completer" ? "Compléter" : "Corriger"}
                    </button>
                  )}
                  {l.source === "mail" && (
                    <button type="button" onClick={() => basculer(l)} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg border border-border text-xs hover:bg-muted">
                      {l.statut === "ignoree" ? <><Eye className="h-3.5 w-3.5" /> Reprendre</> : <><EyeOff className="h-3.5 w-3.5" /> Écarter</>}
                    </button>
                  )}
                </div>
                {edition === l.id && <Correction ligne={l} onFini={() => { setEdition(null); charger(); }} />}
              </div>
            ))}
          </div>
          {ecartees > 0 && (
            <button type="button" onClick={() => setVoirEcartees(!voirEcartees)} className="text-xs text-blue-500 hover:underline">
              {voirEcartees ? "Masquer" : "Voir"} les {ecartees} pièce(s) écartée(s) (pas des factures, doublons)
            </button>
          )}
        </>
      )}
    </div>
  );
}
